import {PIXI, Ease} from '@colonist/pixi'
import {UIGameAchievementConfetti} from './UIGameAchievementConfetti'
import {UIGameAnimation, UIGameMechanic} from '../UIGameEventController'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {UIGameManager} from '../UIGameManager'
import {Sound} from '../../Sound'
import {TranslationObject} from '@colonist/i18n'
import {UIGameBaseAchievementView} from './UIGameBaseAchievementView'
import {UIGameCityImprovementAchievementView} from './UIGameCityImprovementAchievementView'
import {UIGameFirstTimeClaimedAchievementView} from './UIGameFirstTimeClaimedAchievementView'
import {UIGameStolenAchievementView} from './UIGameStolenAchievementView'
import {TimeUtils} from '../../../../../shared/utils/TimeUtils'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {PlayerColor} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums

const TIME = {
    ACHIEVEMENT_ARRIVE: 500,
    ACHIEVEMENT_LEAVE: 500,
    CONFETTI_WAIT: 0,
    CONFETTI_FADE: 500,
}

export interface InGameAchievement {
    title: TranslationObject
    achievementOwnerNew: PlayerColor
    achievementOwnerPrevious: PlayerColor
    achievementDurationInSeconds: number
    soundType?: Sound.SoundType
    achievementScore?: number
    victoryPointChange: number
    bottomDescription: TranslationObject
    iconTexture: PIXI.Texture
    iconTextureSecondLayer?: PIXI.Texture
}

export abstract class UIGameAchievementController extends PIXI.Container implements UIGameMechanic, UIGameAnimation {
    protected readonly uiGameManager: UIGameManager

    private readonly achievements: PIXI.Container
    private readonly ease: Ease
    private active: boolean
    private readonly queue: InGameAchievement[]
    private readonly confetti: UIGameAchievementConfetti

    constructor(uiGameManager: UIGameManager) {
        super()

        this.zIndex = 1
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)

        const canvas = this.uiGameManager.canvasController
        canvas.app.stage.addChild(this)
        this.ease = new Ease({useRaf: false})
        this.achievements = this.addChild(new PIXI.Container())
        this.confetti = this.addChild(new UIGameAchievementConfetti())
        this.queue = []
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.PlayerReceivedAchievement: this.achieve(data); break
            default: break
        }
    }

    onGameTick(deltaTime: number, elapsedMS: number) {
        this.confetti.update(deltaTime, elapsedMS)
    }

    private launchConfetti(achievement: UIGameBaseAchievementView) {
        const canvas = this.uiGameManager.canvasController
        const width = canvas.appW - canvas.chatWidth
        this.confetti.launch({
            x: width - achievement.mainContainer.width / 2,
            y: achievement.mainContainer.height,
            speed: [1.5, 6],
            angle: [0, Math.PI * 2],
            slow: 0.0001,
            gravity: [0.1, 0.2],
            count: 100,
            wait: TIME.CONFETTI_WAIT,
        })
    }

    private achievementEnd() {
        this.active = false
        this.uiGameManager.eventController.unregisterUIGameAnimation(this)

        if(this.queue.length === 0) {
            const confettiFade = this.ease.add(this.confetti, {alpha: 0}, {duration: TIME.CONFETTI_FADE})
            confettiFade.on('complete', () => this.confetti.afterFade())
        } else {
            this.achieve(this.queue.pop() as InGameAchievement)
        }
    }

    private hideAchievement(achievement: UIGameBaseAchievementView, options: InGameAchievement) {
        const position = this.toLocal(new PIXI.Point(), achievement.mainContainer)
        this.addChild(achievement.mainContainer)
        achievement.mainContainer.position = position
        const yEnd = UIUtilsEjsVariables.isUserOnMobileApp ? -achievement.mainContainer.height : achievement.mainContainer.y
        const xEnd = UIUtilsEjsVariables.isUserOnMobileApp ? achievement.mainContainer.x : achievement.mainContainer.x + achievement.mainContainer.width * 1.5
        const animation = this.ease.add(achievement.mainContainer, {
            x: xEnd,
            y: yEnd,
            alpha: 0,
        }, {duration: TIME.ACHIEVEMENT_LEAVE, ease: 'easeOutSine', wait: options.achievementDurationInSeconds * TimeUtils.MILLISECONDS})

        animation.on('complete', () => this.removeChild(achievement.mainContainer))
        animation.on('complete', () => this.achievementEnd())
    }

    private achieve(options: InGameAchievement) {
        if(this.uiGameManager.gameController.isReplay) return

        if(this.active) {
            this.queue.push(options)
        } else {
            this.active = true
            this.uiGameManager.eventController.registerUIGameAnimation(this)
            const achievementView = this.getAchievementView(options)
            this.achievements.addChild(achievementView)
            if(this.parent != undefined) this.parent.sortChildren()

            const showConfettiIfMyAchievement = options.achievementOwnerNew == this.uiGameManager.gameController.myColor
            if(showConfettiIfMyAchievement) this.launchConfetti(achievementView)
            const yStart = this.uiGameManager.canvasController.margin
            const easing = this.ease.add(achievementView, {y: yStart, alpha: 1}, {duration: TIME.ACHIEVEMENT_ARRIVE, ease: 'easeOutBounce'})
            easing.on('complete', () => this.hideAchievement(achievementView, options))
            if(options.soundType != undefined) Sound.playSound(options.soundType)
        }
    }

    protected abstract getAchievementView(options: InGameAchievement): UIGameBaseAchievementView

    protected createAchievementView(options: InGameAchievement, x: number, y: number, w: number, h: number, avatarSize: number): UIGameBaseAchievementView {
        if(options.victoryPointChange == 0) return new UIGameCityImprovementAchievementView(options, this.uiGameManager, x, y, w, h, avatarSize)
        if(options.achievementOwnerPrevious == PlayerColor.None) return new UIGameFirstTimeClaimedAchievementView(options, this.uiGameManager, x, y, w, h, avatarSize)
        return new UIGameStolenAchievementView(options, this.uiGameManager, x, y, w, h, avatarSize)
    }
}
